//* 组件库
import { $message, $loading } from "various-ui";

//* 公共工具库
import { Formats } from "@/assets/js";

//* 公共组件
import type { ItemSearch } from "@/components/controls";
import type { Diagram } from "@/components/visualizations";

//* 私有组件
import type ModuleTutorial from "../components/tutorial/index.vue";

//* 组件属性
import type { ModuleRecommend, ModuleFilter, ModuleQuerys } from "../index";
import { useOptions } from "./options";

//* 样式
import css from "../style/index.module.less";

export const useComposable = () => {
    //* 插件
    const i18n = useI18n();
    const route = useRoute();
    //* 配置项
    const options = useOptions();

    //* 静态数据
    const vars = { className: "" };
    //* 响应式变量
    const refs = {
        recommends: ref<ModuleRecommend[]>([]), //* 推荐列表
        title: ref<string>(""), //* 检索结果标题
        keyword: ref<string>(""), //* 检索列表关键字
        keyword_search: ref<string>(""), //* 检索列表关键字（当前）
        data: ref<any[]>([]), //* 表格数据
        sort: ref<string>(""),

        //* 下拉筛选项
        gene: ref<string>(""),
        genes: ref<ModuleFilter[]>([]),
        mrna: ref<string>(""),
        mrnas: ref<ModuleFilter[]>([]),
        clinical: ref<string>(""),
        clinicals: ref<ModuleFilter[]>([]),
        checkbox: ref<any[]>(["mutation_protein", "variant_type", "clinical", "related_diseases", "references"]),
        checkboxs: ref<boolean>(false),
        visible: ref<boolean>(false),

        //* 分页配置
        page: ref<number>(1),
        count: ref<number>(0),

        //* 搜索框检索结果
        items: ref<ModuleRecommend[]>([]),
        search: ref<ModuleQuerys>({
            genes: [],
            diseases: [],
            mutations: [],
        }),
    };

    const nodes = {
        tutorial: ref<InstanceType<typeof ModuleTutorial>>(),
        candidates: ref<HTMLElement>(),
        item_search: ref<InstanceType<typeof ItemSearch>>(),
        table: ref<InstanceType<typeof Diagram>>(),
    };

    //* 计算属性
    const computeds = {
        //* 是否展示检索列表
        is_search: computed(() => !!(refs.search.value.genes.length || refs.search.value.diseases.length || refs.search.value.mutations.length)),
        option: computed(() => options.table.filter((value: any) => !value.hidden || refs.checkbox.value.find((checkbox) => value.key == checkbox))),
    };

    //* 函数列表
    const methods = {
        //* 初始化函数
        init: async () => {
            //* 获取路由参数
            refs.search.value.genes = Formats.querys(route.query.genes);
            refs.search.value.diseases = Formats.querys(route.query.diseases);
            refs.search.value.mutations = Formats.querys(route.query.mutations);

            if (!computeds.is_search.value) {
                //* 获取推荐列表
                await api.server("VariantRecommends", "/api/bio/variant/recommends", {
                    success: (response) => {
                        refs.recommends.value = response.value || [];
                    },
                });
            } else {
                //* 获取配置列表
                await api.server("Variant", "/api/bio/variant", {
                    method: "POST",
                    data: refs.search.value,
                    success: (response) => {
                        console.log("init", response);
                        //* 数据更新
                        refs.items.value = response.value?.items || [];
                        refs.genes.value = response.value?.genes || [];
                        refs.mrnas.value = response.value?.mrnas || [];
                        refs.clinicals.value = response.value?.clinicals || [];
                        refs.data.value = response.value?.data || [];
                        refs.count.value = response.value?.count || 0;
                        refs.title.value = response.value?.title || "";
                        refs.page.value = 1;
                    },
                });
            }
        },

        //* 检索函数
        enter: (result: any) => {
            if (!result.length) {
                return $message.warning(
                    computeds.is_search.value ? i18n.t("PleaseEnterAGeneDiseaseOrMutation") : i18n.t("PleaseSelectAFieldFromTheRecommendations")
                );
            } else {
                //* 路由参数
                const query = { genes: <string[]>[], diseases: <string[]>[], mutations: <string[]>[] };
                result.forEach((value: any) => {
                    switch (value.type.toLocaleLowerCase()) {
                        case "gene":
                            return query.genes.push(value.id);
                        case "disease":
                            return query.diseases.push(value.id);
                        case "mutation":
                            return query.mutations.push(value.name);
                    }
                });
                routerPush({ name: "BioVariant", query: query });
            }
        },

        //* 检索列表关键字搜索
        search: () => {
            //* 数据缓存
            refs.keyword_search.value = refs.keyword.value.trim();
            //* 加载器
            $loading.open({ message: "" });
            api.frontend("/api/bio/variant", {
                method: "POST",
                data: Object.assign(refs.search.value, { keyword: refs.keyword_search.value }),
                success: (response) => {
                    //* 数据更新
                    refs.genes.value.forEach(
                        (value) => (value.count = response?.genes.find((v: ModuleFilter) => v.value === value.value)?.count || 0)
                    );
                    refs.mrnas.value.forEach(
                        (value) => (value.count = response?.mrnas.find((v: ModuleFilter) => v.value === value.value)?.count || 0)
                    );
                    refs.clinicals.value.forEach(
                        (value) => (value.count = response?.clinicals.find((v: ModuleFilter) => v.value === value.value)?.count || 0)
                    );

                    refs.data.value = response?.data || [];
                    refs.count.value = response?.count || 0;

                    refs.page.value = 1;
                },
                finally: () => $loading.close(),
            });
        },

        //* 重置
        reset: (only_keyword?: boolean) => {
            //* 数据重置
            refs.keyword.value = "";
            refs.keyword_search.value = "";
            if (!only_keyword) {
                refs.gene.value = "";
                refs.mrna.value = "";
                refs.clinical.value = "";
            }

            //* 获取数据
            methods.sort({ sort: { key: "", value: "" } });
        },

        //* 显示列
        show: () => (refs.checkboxs.value = refs.checkbox.value.length == options.rows.length),
        //* 显示全部列
        show_all: () => {
            //* 数据更新
            if (refs.checkboxs.value) {
                options.rows.forEach((value: any) => {
                    if (!refs.checkbox.value.includes(value.value)) refs.checkbox.value.push(value.value);
                });
            } else {
                refs.checkbox.value = [];
            }
        },

        //* 显示候选框
        open: () => {
            refs.visible.value = !refs.visible.value;
            if (refs.visible.value) {
                document.addEventListener("click", methods.hidden, { once: true });
            } else {
                document.removeEventListener("click", methods.hidden);
            }
        },

        //* 隐藏列 候选框隐藏
        hidden: (event: MouseEvent) => {
            if (!nodes.candidates.value) return;
            if (!nodes.candidates.value.contains(event.target as Node)) return (refs.visible.value = false);
            else {
                requestAnimationFrame(() => {
                    document.addEventListener("click", methods.hidden, { once: true });
                });
            }
        },

        //* 表格数据获取
        acquire: (page?: number) => {
            //* 加载器
            $loading.open({ message: "" });

            //* 接口请求
            api.frontend("/api/bio/variant/list", {
                data: Object.assign(refs.search.value, {
                    keyword: refs.keyword_search.value,
                    page: page || refs.page.value,
                    filter_gene_id: refs.gene.value,
                    filter_mutation_type: refs.mrna.value,
                    filter_clinvar_state: refs.clinical.value,
                    sort: refs.sort.value,
                }),
                method: "POST",
                finally: () => $loading.close(),
                success: (response) => {
                    //* 数据更新
                    refs.data.value = response?.data || [];
                    refs.count.value = response?.count || 0;
                    refs.page.value = response?.page || 1;
                    //* 回到顶部
                    scrollTo({ top: 0, behavior: "smooth" });
                },
            });
        },

        //* 表格排序函数
        sort: (result: { sort?: { key: string; value: string } }) => {
            //* 数据更新
            if (!result.sort) return;
            if (result.sort.value == "asc") refs.sort.value = options.sort[result.sort.key];
            else if (result.sort.value == "desc") refs.sort.value = `-${options.sort[result.sort.key]}`;
            else {
                refs.sort.value = "";
                nodes.table.value?.sort("", false, "");
            }

            //* 数据获取
            methods.acquire(1);
        },

        //* 表格额外属性处理
        external_attrs: (datas: any[], data: any, index: number) => {
            const upper = datas?.[index - 1];
            if (!upper) vars.className = "";
            else if (upper?.clinvar != data?.clinvar) {
                vars.className = vars.className == css.active ? "" : css.active;
            }

            return { class: vars.className };
        },

        //* 图文跳转函数
        image: () => open(routerResolve({ name: "VariantTutorials" }).href, "_blank"),
    };

    //* 侦听器
    const watchs = {
        stop: watch(
            () => route.fullPath,
            () => {
                methods.init();
            }
        ),
        items: watch(
            () => refs.items.value,
            () => {
                //* 搜索项更新
                nodes.item_search.value?.update(refs.items.value);
            }
        ),
    };

    //* 动态属性
    const binds = reactive({
        select: { size: "middle" as "middle", width: 176 },

        diagram: computed(() => {
            return { width: 2100, data: refs.data.value, option: computeds.option.value, empty: i18n.t("NoData") };
        }),

        selector: {
            type: "text",
            value: i18n.t("ShowHideColumns"),
            readonly: true,
        },
    });

    return { refs, nodes, binds, watchs, methods, options, computeds };
};
